import { app } from '@/lib/firebase-config';
import { logger } from '@/lib/logging/logger';

// Type definitions for Self-Assessment Funnel Events
export type SelfAssessmentStep = 'start' | 'lead_intake' | 'anterior' | 'lateral_left' | 'lateral_right' | 'posterior' | 'review' | 'report';

/** Steps that show a photo capture control. Contact and PDF steps are not photo prompts. */
const PHOTO_PROMPT_STEPS = new Set<SelfAssessmentStep>([
  'anterior',
  'lateral_left',
  'lateral_right',
  'posterior',
]);

export interface SelfAssessmentEventData {
  step?: SelfAssessmentStep;
  viewName?: string;
  fileType?: string;
  fileSize?: number;
  errorMessage?: string;
  processingTimeMs?: number;
  [key: string]: unknown;
}

let analyticsInstance: any = null;
let isInitialized = false;

/**
 * Safely initialize Firebase Analytics on the client side if supported.
 */
async function getAnalyticsInstance() {
  if (typeof window === 'undefined') {
    return null;
  }

  if (isInitialized) {
    return analyticsInstance;
  }

  try {
    const { getAnalytics, isSupported } = await import('firebase/analytics');
    const supported = await isSupported();
    if (supported && app) {
      analyticsInstance = getAnalytics(app);
      logger.info('[📊 Analytics] Firebase Analytics initialized successfully.');
    } else {
      logger.debug('[📊 Analytics] Firebase Analytics is not supported in this environment.');
    }
  } catch (error) {
    logger.warn('[📊 Analytics] Failed to initialize Firebase Analytics:', error);
  } finally {
    isInitialized = true;
  }

  return analyticsInstance;
}

/**
 * Dispatch an analytics event safely to Firebase Analytics & central application log.
 */
export async function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  const payload = {
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.pathname : '',
    ...eventParams,
  };

  logger.info(`[📊 Analytics Event: ${eventName}]`, payload);

  // Keepalive ingest first — GHL `_top` thank-you unloads Harry before Firebase Analytics finishes.
  if (typeof window !== 'undefined') {
    try {
      fetch('/api/public/analytics/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventName, eventData: payload }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Ignore fetch errors
    }
  }

  try {
    const analytics = await getAnalyticsInstance();
    if (analytics) {
      const { logEvent } = await import('firebase/analytics');
      logEvent(analytics, eventName, payload);
    }
  } catch (err) {
    logger.debug(`[📊 Analytics] Failed to log event ${eventName}:`, err);
  }
}

/**
 * Helper to log step-by-step navigation in Self-Assessment mode.
 */
export function trackSelfAssessmentStep(step: SelfAssessmentStep, extraData?: SelfAssessmentEventData) {
  const deviceType = typeof window !== 'undefined' ? (window.innerWidth < 768 ? 'mobile' : 'desktop') : undefined;
  const payload = { deviceType, ...extraData };
  if (step === 'start') {
    trackEvent('self_assessment_page_view', { step, mode: 'self-assessment', ...payload });
    trackEvent('assessment_started', { step, mode: 'self-assessment', ...payload });
  } else if (PHOTO_PROMPT_STEPS.has(step)) {
    trackEvent('picture_upload_prompted', { step, mode: 'self-assessment', ...payload });
  }
  return trackEvent('self_assessment_step_viewed', {
    step,
    mode: 'self-assessment',
    ...payload,
  });
}

/**
 * Helper to log when a user initiates/selects a photo upload in Self-Assessment mode.
 */
export function trackPhotoUploadStart(viewName: string, fileDetails?: { fileType?: string; fileSize?: number }) {
  trackEvent('picture_selected', {
    viewName,
    mode: 'self-assessment',
    fileType: fileDetails?.fileType,
    fileSize: fileDetails?.fileSize,
  });
  return trackEvent('self_assessment_photo_upload_start', {
    viewName,
    mode: 'self-assessment',
    fileType: fileDetails?.fileType,
    fileSize: fileDetails?.fileSize,
  });
}

/**
 * Helper to log when photo processing and landmark detection succeed.
 */
export function trackPhotoUploadSuccess(viewName: string, processingTimeMs?: number) {
  trackEvent('picture_upload_success', {
    viewName,
    mode: 'self-assessment',
    processingTimeMs,
  });
  return trackEvent('self_assessment_photo_upload_success', {
    viewName,
    mode: 'self-assessment',
    processingTimeMs,
  });
}

/**
 * Downscale an image source to a lightweight JPEG Data URL (max dimension 600px, ~30-80KB).
 */
export async function downscaleImageToJpegDataUrl(
  photoInput: string | HTMLCanvasElement | HTMLImageElement,
  maxDimension = 600,
  quality = 0.5
): Promise<string> {
  return new Promise((resolve) => {
    try {
      if (typeof window === 'undefined') return resolve('');

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve('');

      const processSource = (imgWidth: number, imgHeight: number, drawSource: CanvasImageSource) => {
        let width = imgWidth;
        let height = imgHeight;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(drawSource, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };

      if (typeof photoInput === 'string') {
        if (!photoInput) return resolve('');
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => processSource(img.width, img.height, img);
        img.onerror = () => resolve('');
        img.src = photoInput;
      } else if (photoInput instanceof HTMLCanvasElement) {
        processSource(photoInput.width, photoInput.height, photoInput);
      } else if (typeof HTMLImageElement !== 'undefined' && photoInput instanceof HTMLImageElement) {
        if (photoInput.complete && photoInput.naturalWidth > 0) {
          processSource(photoInput.naturalWidth, photoInput.naturalHeight, photoInput);
        } else {
          photoInput.onload = () => processSource(photoInput.naturalWidth, photoInput.naturalHeight, photoInput);
          photoInput.onerror = () => resolve('');
        }
      } else {
        resolve('');
      }
    } catch {
      resolve('');
    }
  });
}

/**
 * Helper to log when a photo upload or processing fails in Self-Assessment mode,
 * and optionally capture a downscaled sample of the errored photo.
 */
export function trackPhotoUploadError(
  viewName: string,
  errorMessage: string,
  photoInput?: string | HTMLCanvasElement | HTMLImageElement
) {
  trackEvent('picture_upload_failed', {
    viewName,
    mode: 'self-assessment',
    errorMessage,
  });

  if (photoInput && typeof window !== 'undefined') {
    downscaleImageToJpegDataUrl(photoInput)
      .then((dataUrl) => {
        if (dataUrl) {
          const deviceType = window.innerWidth < 768 ? 'mobile' : 'desktop';
          fetch('/api/public/analytics/error-photo-sample', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              viewName,
              errorMessage,
              photoDataUrl: dataUrl,
              deviceType,
            }),
          }).catch(() => {});
        }
      })
      .catch(() => {});
  }

  return trackEvent('self_assessment_photo_upload_error', {
    viewName,
    mode: 'self-assessment',
    errorMessage,
  });
}

/**
 * Helper to log when a captured photo is removed by the user.
 */
export function trackPhotoRemoved(viewName: string) {
  return trackEvent('self_assessment_photo_removed', {
    viewName,
    mode: 'self-assessment',
  });
}

/**
 * Helper to log completion of the self-assessment report.
 */
export function trackSelfAssessmentCompleted(totalViewsCaptured: number) {
  return trackEvent('self_assessment_completed', {
    totalViewsCaptured,
    mode: 'self-assessment',
  });
}

/**
 * Both required photos were uploaded and a live posture classification was produced.
 * Distinct from per-photo Photos OK (`picture_upload_success`) and from survey submit.
 */
export function trackSelfAssessmentAnalysisSuccess(extraData?: SelfAssessmentEventData) {
  const deviceType = typeof window !== 'undefined' ? (window.innerWidth < 768 ? 'mobile' : 'desktop') : undefined;
  return trackEvent('self_assessment_analysis_success', {
    mode: 'self-assessment',
    deviceType,
    ...extraData,
  });
}

/**
 * Helper to log when a user actually downloads/exports the posture PDF report.
 */
export function trackReportPdfDownloaded(extraData?: Record<string, any>) {
  return trackEvent('report_pdf_downloaded', {
    mode: 'self-assessment',
    ...extraData,
  });
}

